// Customise toolbar for My Dashboard: edit mode, saved views, reset to default.
import { useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, Check, Save, RotateCcw, Trash2, Wand2, Loader2, Lock, Unlock } from "lucide-react";

const btn = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-[12.5px] transition-colors";

export default function LayoutToolbar({ dl, editing, setEditing, counts, sales }) {
  const [naming, setNaming] = useState(false);
  const [opt, setOpt] = useState(false);
  const optimise = async () => {
    setOpt(true);
    try { await dl.optimise(counts, sales); toast("Preview ready — Keep or Cancel above your boxes"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't optimise"); } finally { setOpt(false); }
  };
  const [name, setName] = useState("");
  const submit = (e) => { e.preventDefault(); const n = name.trim(); if (!n) return; dl.saveView(n); setNaming(false); setName(""); };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="dash-layout-toolbar">
      {dl.views.length > 0 && (
        <select value={dl.active || ""} onChange={(e) => e.target.value && dl.applyView(e.target.value)} data-testid="dash-view-select"
          className="pl-3 pr-8 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-[12.5px] text-slate-200 cursor-pointer">
          <option value="">{dl.active ? "Saved views" : "Custom layout"}</option>
          {dl.views.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
        </select>
      )}
      {editing && <>
        <button onClick={optimise} disabled={opt || dl.previewing} data-testid="dash-layout-optimise" title="Auto-size and order boxes by what you've got on"
          className={`${btn} border-amber-400/50 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20 disabled:opacity-50`}>
          {opt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Optimise
        </button>
        {naming ? (
          <form onSubmit={submit} className="flex items-center gap-1.5">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="View name…" data-testid="dash-view-name-input"
              className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/15 focus:border-teal-400/50 outline-none text-[12.5px] text-slate-200 w-40" />
            <button type="submit" className={`${btn} border-teal-400/50 bg-teal-500/15 text-teal-200`} data-testid="dash-view-name-save">Save</button>
            <button type="button" onClick={() => setNaming(false)} className={`${btn} border-white/10 text-slate-400`} data-testid="dash-view-name-cancel">Cancel</button>
          </form>
        ) : (
          <button onClick={() => { setName(dl.active || ""); setNaming(true); }} className={`${btn} border-white/10 bg-white/[0.03] text-slate-200 hover:bg-white/[0.07]`} data-testid="dash-view-save">
            <Save className="w-3.5 h-3.5" /> Save as view
          </button>
        )}
        {dl.active && <button onClick={() => dl.deleteView(dl.active)} className={`${btn} border-white/10 bg-white/[0.03] text-rose-300 hover:border-rose-400/40`} data-testid="dash-view-delete" title={`Delete view "${dl.active}"`}><Trash2 className="w-3.5 h-3.5" /></button>}
        <button onClick={dl.reset} className={`${btn} border-white/10 bg-white/[0.03] text-slate-200 hover:bg-white/[0.07]`} data-testid="dash-layout-reset"><RotateCcw className="w-3.5 h-3.5" /> Reset to default</button>
        <label className={`${btn} border-white/10 bg-white/[0.03] text-slate-200 cursor-pointer`} title="When off, your boxes are tidied automatically once a week (empty boxes move down, heights fit content)" data-testid="dash-layout-lock">
          <input type="checkbox" checked={dl.locked} onChange={(e) => { dl.setLocked(e.target.checked); toast(e.target.checked ? "Layout locked — weekly tidy will skip you" : "Weekly tidy is on"); }} data-testid="dash-layout-lock-input" />
          {dl.locked ? <Lock className="w-3.5 h-3.5 text-amber-300" /> : <Unlock className="w-3.5 h-3.5" />} Keep my layout
        </label>
      </>}
      <button onClick={() => setEditing((v) => !v)} data-testid="dash-customise-toggle"
        className={`${btn} ${editing ? "border-teal-400/60 bg-teal-500/20 text-teal-100" : "border-white/10 bg-white/[0.03] text-slate-200 hover:bg-white/[0.07]"}`}>
        {editing ? <><Check className="w-3.5 h-3.5" /> Done</> : <><LayoutGrid className="w-3.5 h-3.5" /> Customise</>}
      </button>
    </div>
  );
}
